import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const EFFECTIVE_DATE = '5 July 2026'
const COMPANY = 'DS Commercial Group Ltd'
const TRADING = 'Rebound'
const REGISTERED = 'England and Wales'
const COMPANY_NUMBER = ''
const REGISTERED_OFFICE = ''
const VAT_NUMBER = ''
const LEGAL_EMAIL = 'legal@reboundapp.co.uk'
const SUPPORT_EMAIL = 'support@reboundapp.co.uk'
const PLATFORM_URLS = 'reboundapp.co.uk and reboundservice.netlify.app'
const MONTHLY_PRICE = 'GBP 99.99 per month'
const SELF_SERVICE_TRIAL = '90-day free trial'

const subprocessors = [
  'Supabase - database hosting, authentication and file/data storage',
  'Netlify - application hosting and serverless functions',
  'Stripe - subscription billing and payment processing where enabled',
  'Resend - transactional email delivery',
  'Anthropic - optional AI-assisted service menu parsing when a workshop uses that feature'
]

function companyDetailsLine() {
  const details = [`${COMPANY}, trading as ${TRADING}, is registered in ${REGISTERED}.`]
  if (COMPANY_NUMBER) details.push(`Company number: ${COMPANY_NUMBER}.`)
  if (REGISTERED_OFFICE) details.push(`Registered office: ${REGISTERED_OFFICE}.`)
  if (VAT_NUMBER) details.push(`VAT number: ${VAT_NUMBER}.`)
  return details.join(' ')
}

const docs = {
  terms: {
    label: 'Terms',
    title: 'Terms of Service',
    intro: [
      `These Terms govern access to and use of ${TRADING}, a workshop management platform for bicycle suspension service businesses.`,
      `These Terms are intended for business customers only. If you are buying as a consumer, tell us before subscribing so we can confirm whether different rights apply.`
    ],
    sections: [
      {
        title: '1. Who we are',
        body: [
          companyDetailsLine(),
          `You can contact us at ${LEGAL_EMAIL}. Product support is available at ${SUPPORT_EMAIL}.`
        ]
      },
      {
        title: '2. The service',
        body: [
          `${TRADING} provides software tools for workshops to manage customers, bikes, suspension components, service jobs, service reports, bookings, reminders, workshop services, team access and billing records.`,
          `We grant the workshop a non-exclusive, non-transferable, revocable licence to access and use the platform while the workshop has an active subscription and complies with these Terms.`
        ]
      },
      {
        title: '3. Workshop accounts and users',
        body: [
          'The workshop is responsible for all activity under its account, including activity by owners, technicians, administrators and invited team members.',
          'Each user must keep login details secure and must not share passwords. The workshop must remove access for staff who leave or no longer need access.',
          'A subscription covers one workshop business/location unless we agree otherwise in writing.'
        ]
      },
      {
        title: '4. Subscriptions, billing and payment',
        body: [
          `Unless agreed otherwise in writing, the standard subscription price is ${MONTHLY_PRICE}, charged monthly in advance through Stripe.`,
          `Self-service signup includes one lifetime ${SELF_SERVICE_TRIAL} per workshop business, with card details collected at signup. After the trial, the paid subscription starts automatically unless cancelled before the trial ends.`,
          'The 12-month minimum term starts only after the first successful paid invoice following the trial, not on the first day of the free trial.',
          'Payments are handled by Stripe. We do not store full card or bank account details in Rebound.',
          'If a payment fails, we may retry collection and may suspend access if payment remains overdue.'
        ]
      },
      {
        title: '5. Cancellation and minimum terms',
        body: [
          'The applicable minimum term, cancellation notice period and payment date will be shown at sign-up or agreed with the workshop in writing.',
          'Unless a different cancellation term is agreed, cancellation takes effect at the end of the current paid billing period. Access continues until the end of the paid period.',
          'Fees already paid are non-refundable except where required by law or where we agree otherwise in writing.'
        ]
      },
      {
        title: '6. Workshop responsibilities',
        body: [
          'The workshop is responsible for the accuracy, lawfulness and completeness of all data entered into the platform.',
          'The workshop must have a lawful basis to collect and store customer personal data, including customer names, emails, phone numbers, bike details, component details and service records.',
          'The workshop is responsible for its own repair work, customer communication, pricing, workshop services, consumer terms, tax, invoices, safety checks and professional obligations.',
          'The workshop must not use Rebound for unlawful, harmful, abusive, fraudulent or unauthorised activity.'
        ]
      },
      {
        title: '7. Booking widget and workshop customers',
        body: [
          'The public booking widget allows a customer to request a booking with a workshop. Rebound provides the technology only.',
          'The repair or service contract is between the workshop and its customer. Rebound is not a party to that repair or service contract.',
          'The workshop is responsible for confirming accepted bookings, proposed dates, prices, cancellation terms, delivery arrangements, deposits and any other customer-facing terms required by law.',
          'A booking request is not guaranteed until the workshop accepts it.'
        ]
      },
      {
        title: '8. Reports, payment links and third-party services',
        body: [
          'Rebound can generate service reports and payment links. The workshop is responsible for checking report content before sending it to customers.',
          'Where payment links or card payments are enabled, they are provided through Stripe. Stripe terms and processing rules apply to payments made through Stripe.',
          'Rebound may integrate with third-party services such as Supabase, Netlify, Stripe, Resend and Anthropic to operate platform features.'
        ]
      },
      {
        title: '9. Data ownership and platform IP',
        body: [
          'The workshop retains ownership of customer, bike, component, job and service data entered into the platform.',
          'The workshop grants us the rights needed to host, process, transmit, display and back up that data so we can provide the platform.',
          `All software, design, code, branding, workflows and product features in ${TRADING} belong to ${COMPANY} or its licensors. The workshop must not copy, reverse engineer, resell or sublicense the platform.`
        ]
      },
      {
        title: '10. Availability and support',
        body: [
          'We aim to provide a reliable service, but we do not guarantee uninterrupted availability. Planned maintenance, provider outages, internet failures and emergency fixes may affect access.',
          `Support requests should be sent to ${SUPPORT_EMAIL}.`
        ]
      },
      {
        title: '11. Suspension and termination',
        body: [
          'We may suspend or terminate access if the workshop breaches these Terms, fails to pay, creates security risk, misuses the service, or uses the platform unlawfully.',
          'On termination, the data export and deletion terms in the Privacy Policy and Data Processing Agreement apply.'
        ]
      },
      {
        title: '12. Liability',
        body: [
          'Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, fraudulent misrepresentation, or any liability that cannot legally be limited.',
          'Subject to that, we are not liable for indirect loss, loss of profit, loss of revenue, loss of goodwill, business interruption, or loss caused by incorrect data entered by the workshop.',
          'Our total liability for claims arising in any 12-month period is limited to the fees paid by the workshop for Rebound during that 12-month period.'
        ]
      },
      {
        title: '13. Changes',
        body: [
          'We may update these Terms from time to time. If a change is material, we will give reasonable notice by email or in-app notice.',
          'Continuing to use the platform after the effective date of updated Terms means the workshop accepts the updated Terms.'
        ]
      },
      {
        title: '14. Law and jurisdiction',
        body: [
          'These Terms are governed by the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction, unless applicable law requires otherwise.'
        ]
      }
    ]
  },
  privacy: {
    label: 'Privacy',
    title: 'Privacy Policy',
    intro: [
      `This Privacy Policy explains how ${COMPANY}, trading as ${TRADING}, handles personal data when you use the Rebound platform.`,
      'It covers workshop account users and the customer/service data that workshops store in Rebound.'
    ],
    sections: [
      {
        title: '1. Our data protection roles',
        body: [
          'For workshop account, billing, support and usage data, we usually act as the data controller.',
          'For customer, bike, component, booking and service job data entered by a workshop, the workshop is usually the data controller and we act as its data processor.',
          `Contact us at ${LEGAL_EMAIL} for privacy queries.`
        ]
      },
      {
        title: '2. Personal data we collect',
        body: [
          'Workshop account data: names, email addresses, roles, login details, workshop name, workshop address, billing details and team invitations.',
          'Workshop customer data: customer names, email addresses, phone numbers, bike details, component details, booking requests, service records, service due dates, notes and reports.',
          'Payment data: Stripe customer IDs, subscription IDs, invoice/payment status and payment links. Full card details are handled by Stripe and are not stored by Rebound.',
          'Technical data: IP address, browser/device information, authentication logs, security logs and diagnostic data.',
          'AI parsing data: when a workshop uses the AI service menu parser, the pasted service menu text is sent to Anthropic to return structured services.'
        ]
      },
      {
        title: '3. How we use personal data',
        body: [
          'To provide, secure, operate and improve the platform.',
          'To authenticate users and manage team access.',
          'To process subscriptions, payment links and billing records.',
          'To send service-related emails such as booking confirmations, declined booking responses, reminders, reports, invites and billing notices.',
          'To respond to support requests and troubleshoot issues.',
          'To comply with legal, accounting, tax and security obligations.'
        ]
      },
      {
        title: '4. Lawful bases',
        body: [
          'Contract: to provide the platform and manage paid subscriptions.',
          'Legitimate interests: to secure, maintain and improve the platform, prevent misuse and support customers.',
          'Legal obligation: to keep records required for tax, accounting and compliance.',
          'Processor instructions: where we process workshop customer data on behalf of a workshop.'
        ]
      },
      {
        title: '5. Subprocessors and sharing',
        body: [
          'We do not sell personal data.',
          'We use trusted subprocessors to operate the platform:',
          { type: 'list', items: subprocessors },
          'We may also share data where required by law, to protect legal rights, or with professional advisers such as accountants, insurers or legal advisers.'
        ]
      },
      {
        title: '6. International transfers',
        body: [
          'Some subprocessors may process data outside the UK. Where this happens, we rely on appropriate safeguards such as UK-approved transfer terms, adequacy arrangements, or equivalent contractual/data protection protections.'
        ]
      },
      {
        title: '7. Data retention',
        body: [
          'Workshop data is retained while the subscription is active.',
          'After cancellation or termination, we aim to keep workshop data available for export for 90 days, then delete or anonymise it unless we are legally required to keep it longer.',
          'Billing, tax and accounting records may be retained for up to 7 years.',
          'Security logs and backups may be retained for a limited period for operational and security purposes.'
        ]
      },
      {
        title: '8. Security',
        body: [
          'We use technical and organisational measures designed to protect personal data, including authenticated access, role-based access, HTTPS, Supabase row-level security, restricted service keys and operational security controls.',
          'No system is completely secure. Workshops must also keep their own logins, devices and staff access secure.'
        ]
      },
      {
        title: '9. Cookies and local storage',
        body: [
          'The platform uses essential cookies and local storage for authentication, session management, preferences and draft recovery. We do not currently use advertising cookies in the application.'
        ]
      },
      {
        title: '10. Individual rights',
        body: [
          'Individuals may have rights to access, correct, delete, restrict, object to processing, or receive a copy of their personal data.',
          'Workshop customer requests should normally be made to the workshop first, because the workshop controls that customer relationship. We will assist workshops with valid requests where required.'
        ]
      },
      {
        title: '11. Complaints',
        body: [
          `You can contact us at ${LEGAL_EMAIL}. If you are unhappy with how personal data is handled, you can complain to the Information Commissioner's Office at ico.org.uk.`
        ]
      }
    ]
  },
  dpa: {
    label: 'Data Processing',
    title: 'Data Processing Agreement',
    intro: [
      'This Data Processing Agreement forms part of the Terms of Service where a workshop uses Rebound to process personal data about its customers, staff, technicians or contacts.',
      'It is written as a practical UK GDPR processor schedule for Rebound workshops.'
    ],
    sections: [
      {
        title: '1. Roles',
        body: [
          'For workshop customer data, the workshop is the controller and Rebound is the processor.',
          'For Rebound account, billing, security and support data, Rebound may act as an independent controller.'
        ]
      },
      {
        title: '2. Processing details',
        body: [
          'Subject matter: hosting and operating workshop management software for suspension service businesses.',
          'Duration: for the active subscription period and any post-termination export/deletion period.',
          'Nature and purpose: storing, organising, displaying, emailing and processing customer, booking, component and service job data so the workshop can run its service operations.',
          'Data subjects: workshop customers, workshop staff, technicians, administrators and invited users.',
          'Categories of personal data: names, contact details, user credentials, roles, workshop details, bike/component details, booking details, service records, notes, invoices/payment status and communication records.'
        ]
      },
      {
        title: '3. Workshop instructions',
        body: [
          'We will process workshop customer data only on documented instructions from the workshop, including these Terms, app configuration, user actions, support instructions and lawful written instructions.',
          'If we believe an instruction breaches data protection law, we may pause processing and notify the workshop.'
        ]
      },
      {
        title: '4. Confidentiality',
        body: [
          'We will ensure that people authorised to process personal data are bound by confidentiality obligations or appropriate duties of confidentiality.'
        ]
      },
      {
        title: '5. Security measures',
        body: [
          'We will maintain appropriate technical and organisational measures designed to protect personal data, including access controls, encrypted transport, role restrictions, database isolation, monitoring and backup practices appropriate to the service.'
        ]
      },
      {
        title: '6. Subprocessors',
        body: [
          'The workshop authorises us to use subprocessors needed to provide the platform:',
          { type: 'list', items: subprocessors },
          'We remain responsible for subprocessors where required by applicable data protection law and will require subprocessors to protect personal data appropriately.'
        ]
      },
      {
        title: '7. International transfers',
        body: [
          'Where personal data is transferred outside the UK, we will use lawful transfer mechanisms and appropriate safeguards where required.'
        ]
      },
      {
        title: '8. Assistance',
        body: [
          'Taking into account the nature of processing, we will provide reasonable assistance to the workshop with data subject rights requests, data protection impact assessments, security obligations and regulator requests where required by law.',
          'If assistance requires substantial manual work outside normal support, we may charge reasonable fees unless the work is needed because of our breach.'
        ]
      },
      {
        title: '9. Personal data breaches',
        body: [
          'We will notify the workshop without undue delay after becoming aware of a personal data breach affecting workshop customer data.',
          'We will provide reasonable information available to us to help the workshop assess and meet any reporting obligations.'
        ]
      },
      {
        title: '10. Deletion and export',
        body: [
          'During the subscription, the workshop may export or request export of its data where available.',
          'After termination, we will delete or anonymise workshop customer data after the post-termination retention period unless law requires retention.'
        ]
      },
      {
        title: '11. Audit information',
        body: [
          'We will make reasonable information available to demonstrate compliance with this DPA. Audits must be reasonable, proportionate, confidential and not compromise the security or confidentiality of other customers.'
        ]
      },
      {
        title: '12. Workshop controller obligations',
        body: [
          'The workshop remains responsible for privacy notices, lawful basis, customer consent where needed, data accuracy, customer communications, retention decisions and responding to customer rights requests.'
        ]
      }
    ]
  },
  cancellation: {
    label: 'Cancellation',
    title: 'Cancellation and Refund Policy',
    intro: [
      'This policy explains how Rebound subscription cancellation, failed payments, refunds and data export work.',
      'Workshop customer repair/service cancellations are controlled by the workshop, not Rebound.'
    ],
    sections: [
      {
        title: '1. Trials and onboarding',
        body: [
          `Self-service signup includes one lifetime ${SELF_SERVICE_TRIAL} per workshop business. Card details are collected at signup so monthly billing can begin automatically after the trial.`,
          'If the workshop does not want to continue after a trial, it must cancel before the paid subscription starts to avoid the first charge.',
          'The 12-month minimum term starts only after the first successful paid invoice following the free trial.'
        ]
      },
      {
        title: '2. Subscription cancellation',
        body: [
          `To cancel, contact ${SUPPORT_EMAIL} or use any cancellation tool made available in the platform.`,
          'Unless agreed otherwise, cancellation takes effect at the end of the current paid billing period.',
          'If the workshop has agreed a minimum term, the workshop remains responsible for fees due during that minimum term.'
        ]
      },
      {
        title: '3. Refunds',
        body: [
          'Subscription payments are charged in advance and are generally non-refundable.',
          'We may provide a refund or credit at our discretion if there has been a duplicate charge, billing error, or other issue we accept as our responsibility.',
          'This does not affect any rights that cannot legally be excluded.'
        ]
      },
      {
        title: '4. Failed payments',
        body: [
          'If payment fails, Stripe or Rebound may retry payment. We may notify the workshop and ask for updated payment details.',
          'If payment remains overdue, we may suspend access until the overdue amount is paid.'
        ]
      },
      {
        title: '5. Data after cancellation',
        body: [
          'We recommend exporting workshop data before cancellation takes effect.',
          'After cancellation or termination, we aim to keep data available for export for 90 days, then delete or anonymise it unless we need to keep it for legal, accounting, security or dispute reasons.'
        ]
      },
      {
        title: '6. Workshop customer bookings',
        body: [
          'The Rebound booking widget collects booking requests for the workshop. Rebound does not decide whether a workshop accepts, rejects, reschedules, refunds or cancels a repair/service booking.',
          'The workshop must provide its own customer-facing cancellation, repair, deposit, shipping, returns and refund terms where required.'
        ]
      }
    ]
  }
}

function renderBlock(block, index) {
  if (typeof block === 'string') return <p key={index}>{block}</p>
  if (block.type === 'list') {
    return (
      <ul key={index}>
        {block.items.map(item => <li key={item}>{item}</li>)}
      </ul>
    )
  }
  return null
}

export default function LegalPage() {
  const [tab, setTab] = useState('terms')
  const navigate = useNavigate()
  const active = docs[tab]

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div className="page-head">
          <div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 14 }}>Back</button>
            <div className="page-title">Legal</div>
            <div className="text-muted fs12 mt4">{COMPANY} trading as {TRADING}</div>
          </div>
          <div className="section-meta">Effective {EFFECTIVE_DATE}</div>
        </div>

        <div className="settings-tabs" style={{ marginBottom: 24 }}>
          {Object.entries(docs).map(([key, doc]) => (
            <button
              key={key}
              className={`btn btn-sm ${tab === key ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setTab(key)}
            >
              {doc.label}
            </button>
          ))}
        </div>

        <div className="legal-body card">
          <h1>{active.title}</h1>
          <p className="meta">Effective date: {EFFECTIVE_DATE} - {COMPANY} trading as {TRADING}</p>

          {active.intro.map((paragraph, index) => <p key={`intro-${index}`}>{paragraph}</p>)}

          {active.sections.map(section => (
            <React.Fragment key={section.title}>
              <h2>{section.title}</h2>
              {section.body.map(renderBlock)}
            </React.Fragment>
          ))}

          <h2>Contact</h2>
          <p>Legal notices: <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a></p>
          <p>Support: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
          <p>Platform: {PLATFORM_URLS}</p>
        </div>
      </div>
    </div>
  )
}
